import { PublicClientApplication } from "@azure/msal-browser";

export const APP_TOKEN_KEY = "agri_app_token";
export const APP_USER_KEY = "agri_app_user";
const AUTH_CONFIG_KEY = "agri_auth_config";

let msalInstance = null;

export const initialize = async () => {
  if (msalInstance) {
    return;
  }
  let cfg = null;

  if (navigator.onLine) {
    try {
      const res = await fetch("/auth");
      if (res.ok) {
        cfg = await res.json();
        localStorage.setItem(AUTH_CONFIG_KEY, JSON.stringify(cfg));
      }
    } catch (error) {
      console.warn("Failed to fetch auth config", error);
    }
  }

  if (!cfg) {
    try {
      const cached = localStorage.getItem(AUTH_CONFIG_KEY);
      cfg = cached ? JSON.parse(cached) : null;
    } catch {
      cfg = null;
    }
  }

  if (!cfg) return;

  const msal = new PublicClientApplication({
    auth: {
      clientId: cfg.clientId,
      authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
      redirectUri: `${window.location.origin}/dashboard`,
    },
    cache: {
      cacheLocation: "localStorage",
      storeAuthStateInCookie: false,
    },
  });

  await msal.initialize();
  msalInstance = msal;
};

export const getMsalInstance = () => msalInstance;

export const login = () => {
  if (!msalInstance) return;
  msalInstance.loginRedirect({
    scopes: ["openid", "profile", "email"],
  });
};

export const logout = async () => {
  const appToken = localStorage.getItem(APP_TOKEN_KEY);

  if (appToken) {
    try {
      await fetch("/api/sessions", {
        method: "DELETE",
        headers: { Authorization: `Bearer ${appToken}` },
      });
    } catch (error) {
      console.warn("Server-side logout failed", error);
    }
  }

  localStorage.removeItem(APP_TOKEN_KEY);
  localStorage.removeItem(APP_USER_KEY);

  if (!msalInstance) {
    await initialize();
  }

  const account = msalInstance?.getActiveAccount();
  msalInstance?.logoutRedirect({
    account,
    postLogoutRedirectUri: window.location.origin,
  });
};

// Exchange a fresh MSAL ID token for a long-lived app token, then cache it.
export const exchangeForAppToken = async () => {
  if (!navigator.onLine) return null;
  if (!msalInstance) await initialize();
  if (!msalInstance) return null;
  const account = msalInstance.getActiveAccount();
  if (!account) return null;

  let msalIdToken;
  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: ["openid", "profile", "email"],
      account,
    });
    msalIdToken = response.idToken;
  } catch {
    msalInstance.acquireTokenRedirect({ scopes: ["openid", "profile", "email"] });
    return null;
  }

  const res = await fetch("/api/sessions", {
    method: "POST",
    headers: { Authorization: `Bearer ${msalIdToken}` },
  });

  if (!res.ok) {
    // Clear any stale app token so it can't be reused by a different MSAL account
    localStorage.removeItem(APP_TOKEN_KEY);
    localStorage.removeItem(APP_USER_KEY);
    return null;
  }
  const { token } = await res.json();
  localStorage.setItem(APP_TOKEN_KEY, token);
  return token;
};

// Returns the cached app token, exchanging for a new one if absent.
export const appToken = async () => {
  const stored = localStorage.getItem(APP_TOKEN_KEY);
  if (stored) return stored;
  return exchangeForAppToken();
};

// Kept for backwards compat — now returns app token, not MSAL ID token.
export const idToken = appToken;

export const token = async () => {
  if (!msalInstance) await initialize();
  if (!msalInstance) return null;
  const account = msalInstance.getActiveAccount();
  if (!account) return null;
  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: ["openid", "profile", "email"],
      account,
    });
    return response.accessToken;
  } catch (error) {
    console.error("Silent token acquisition failed", error);
    msalInstance.acquireTokenRedirect({ scopes: ["openid", "profile", "email"] });
  }
};
